<template>
    <div>
        <!-- Hamburger (only on small screens, only when sidebar is closed) -->
        <div
            class="hamburger"
            v-if="isSmallScreen && !sidebarOpen"
            @click="toggleSidebar"
            :aria-label="$t('nav.openMenu')"
        >
            &#9776;
        </div>

        <!-- Mobile bell next to hamburger -->
        <!-- <div v-if="isSmallScreen && !sidebarOpen" class="mobile-bell" @click="toggleNotificationsPanel">
            <i class="bi bi-bell-fill notification-bell"></i>
            <span v-if="unreadCount > 0" class="notification-badge">{{ unreadCount }}</span>
        </div> -->
        
        <!-- Backdrop behind the mobile drawer — dims the page and closes on tap -->
        <div
            v-if="isSmallScreen && sidebarOpen"
            class="sidebar-backdrop"
            @click="toggleSidebar"
            aria-hidden="true"
        ></div>

        <!-- Sidebar -->
        <div
            class="sidebar"
            :class="{ 'open': sidebarOpen, 'small-screen': isSmallScreen }"
            v-if="!isSmallScreen || sidebarOpen"
        >
            <!-- Close button (mobile) -->
            <button class="close-btn" @click="toggleSidebar" v-if="isSmallScreen" :aria-label="$t('nav.closeSidebar')">
                <i class="bi bi-x-lg"></i>
            </button>

            <!-- Header -->
            <div class="sidebar-head" v-if="!isRegistrationRoute && isLoggedIn">
                <img class="sidebar-logo" :src="logo_storalytic" alt="Logo Storalytic" />
                <button class="bell-btn" @click="toggleNotificationsPanel" :aria-label="$t('nav.notifications')">
                    <i class="bi bi-bell-fill"></i>
                    <span v-if="unreadCount > 0" class="notification-badge">{{ unreadCount }}</span>
                </button>

                <!-- wraps to multiple lines as needed -->
                <div class="company-name">{{ companyName }}</div>
            </div>

            <hr class="thin-divider">            

            <!-- Navigation links -->
            <div class="main-links" v-if="!isRegistrationRoute && isLoggedIn">
                <router-link to="/" class="home-link" exact-active-class="home-link-active"><i class="bi bi-house-door"></i> {{ $t('nav.home') }}</router-link>

                <div class="sub-title"><i class="bi bi-graph-up-arrow"></i> {{ $t('nav.insights') }}</div>
                <router-link to="/store_level" class="nav-link" exact-active-class="active-link" style="padding-left: 20px;">{{ $t('nav.storeLevel') }}</router-link>
                <!-- Promotions is deliberately NOT a fourth tab on Store Level: a promo
                     is user-supplied ground truth about the physical world (price,
                     discount, which items) with its own pinned windows, not a step in
                     the Actions -> Zones -> Explore drill-down. Named "Promotions",
                     never "Actions"-anything — in Dutch "actie" means promotion, and
                     the collision would be total in translation. -->
                <router-link to="/promotions" class="nav-link" exact-active-class="active-link" style="padding-left: 20px;">{{ $t('nav.promotions') }}</router-link>
                
                <div v-for="(group, typeId) in groupedZonesByType" :key="typeId">
                    <div v-if="group.zones.some(z => !!z.DetectionZoneName)">
                        <hr class="thin-divider-sub">
                        <div class="expandable-header-detection-zones" @click="toggleType(typeId)">
                            <span :class="{'triangle-icon': true, 'expanded': expandedTypes[typeId]}"></span>
                            {{ $t(`nav.zoneGroups.${typeId}`) }}
                        </div>

                        <div v-if="expandedTypes[typeId]" class="detection-zone-list">
                            <router-link
                                v-for="zone in group.zones"
                                :key="zone.DetectionZoneId"
                                :to="`/zone/${zone.DetectionZoneId}`"
                                class="flow-link"
                                exact-active-class="active-link"
                            >
                                <span class="geom-glyph" :title="zoneGeometryLabel(zone)">
                                    <i v-if="zoneHasPolygon(zone)" class="bi bi-bounding-box-circles"></i>
                                    <i v-if="zoneHasTripwire(zone)" class="bi bi-arrow-left-right"></i>
                                </span>
                                {{ zone.DetectionZoneName }}
                            </router-link>
                        </div>
                    </div>
                </div>

                <hr class="thin-divider">
                <div class="sub-title"><i class="bi bi-shop"></i> {{ $t('nav.realTime') }}</div>
                <router-link to="/real_time_dashboard" class="nav-link" exact-active-class="active-link" style="padding-left: 20px;">{{ $t('nav.realTimeStoreData') }}</router-link>

                <hr class="thin-divider">
                
                <div class="sub-title"><i class="bi bi-camera-video"></i> {{ $t('nav.cameras') }}</div>
                <!-- <router-link to="/camera_overview" class="nav-link" exact-active-class="active-link" style="padding-left: 20px;">All Camera Overview</router-link> -->
                
                <router-link to="/camera_overview" class="nav-link" exact-active-class="active-link" style="padding-left: 20px;">{{ $t('nav.cameraHealthOverview') }}</router-link>
                
                <div>
                    <!-- Cameras toggle with triangle icon -->
                    <hr class="thin-divider-sub">
                    <div class="expandable-header-cameras" @click="toggleFlowList">
                        <span :class="{'triangle-icon': true, 'expanded': showFlowList}"></span>
                        {{ $t('nav.cameras') }}
                    </div>

                    <!-- Flow names list under Camera Overview, visible only if expanded -->
                    <div v-if="showFlowList" class="flow-list">
                        <router-link
                            v-for="flow in flowStore.flows"
                            :key="flow.FlowId"
                            :to="`/flow/${flow.FlowId}`"
                            class="flow-link"
                            exact-active-class="active-link"
                        >
                            {{ flow.FlowName }}
                        </router-link>
                    </div>
                </div>
                
                <!-- External data sources are hidden until there is one to add: weather (the
                     only source so far) is now part of every store — its location is set at
                     registration and in Settings → Location & calendar. The /external_data and
                     /connect_api routes stay, so this block can simply come back. -->

                <hr v-if="!isSmallScreen" class="thin-divider">
                <div class="sub-title"><i class="bi bi-node-plus"></i> {{ $t('nav.connectNew') }}</div>
                <router-link v-if="!isSmallScreen && !userStore.isRegularUser" to="/create_flow" class="nav-link" exact-active-class="active-link" style="padding-left: 20px;">{{ $t('nav.connectNewCamera') }}</router-link>
                <!-- <hr class="divider">
                <router-link to="/data_analytics" :class="{'active-page': page == 'data_analytics'}" @click="page = 'data_analytics'">Quick Data Analytics</router-link> -->
            </div>

            <!-- Footer section with connection status, settings, and user info -->
            <div class="sidebar-footer" v-if="!isRegistrationRoute && isLoggedIn">
                <hr class="thin-divider" />

                <!-- Connection status -->
                <div class="connection-status">
                    <div class="status-label">{{ $t('nav.edgalyticStatusLabel') }}</div>
                    <span class="chip" :class="serverConnection ? 'chip-ok' : 'chip-warn'">
                    <i class="bi" :class="serverConnection ? 'bi-wifi' : 'bi-wifi-off'"></i>
                    {{ serverConnection ? $t('nav.edgalyticConnected') : $t('nav.edgalyticDisconnected') }}
                    </span>
                </div>

                <hr class="thin-divider" />

                <!-- Settings + user row + switch company -->
                <div class="settings-container">
                    <router-link to="/settings" class="nav-link footer-link" exact-active-class="active-link">
                    <i class="bi bi-gear-fill"></i> {{ $t('nav.settings') }}
                    </router-link>

                    <div class="user-row">
                        <i class="bi bi-person-fill"></i>
                        <span class="user-name">{{ userName }} {{ userSurname }} - {{ userRole }}</span>
                        <span class="dot" aria-hidden="true">•</span>
                        <button type="button" class="logout-link-btn" @click="logout">{{ $t('nav.logout') }}</button>
                    </div>

                    <!-- the reader's language, saved on their account. Super-admins only
                         until the screens are translated: a customer picking Nederlands
                         would get Dutch Allen texts in English pages. -->
                    <LanguagePicker v-if="userStore.isSuperAdmin" />

                    <router-link
                    v-if="userStore.isSuperUser || userStore.isSuperAdmin"
                    to="/select-company"
                    class="nav-link footer-link"
                    exact-active-class="active-link"
                    >
                    <i class="bi bi-arrow-repeat"></i> {{ $t('nav.switchCompany') }}
                    </router-link>
                </div>
            </div>

        </div>

        <NotificationsSidebar v-if="notifCenter.open" :is-visible="notifCenter.open" @close="notifCenter.close()" />

    </div>
</template>
  
<script>
    import axios from 'axios'

    import { ref, onBeforeMount, onMounted, onBeforeUnmount, computed, toRefs, watch } from 'vue'
    import { useRoute, useRouter } from 'vue-router'

    import { useToast } from 'vue-toastification'
    import { useI18n } from 'vue-i18n'

    import { useUserStore } from '@/stores/UserStore'
    import { useCompanyStore } from '@/stores/CompanyStore'
    import { parseDeviceUrl, liveDeviceUrl, isLiveRelayUrl, liveProbeUrl } from '@/api/deviceUrl'
    import { useFlowStore } from '@/stores/FlowStore'
    import { useCameraStreamStore } from '@/stores/CameraStreamStore'
    import { useDetectionZoneStore } from '@/stores/DetectionZone'
    import { useZoneDirectoryStore } from '@/stores/ZoneDirectory'
    import { useNotificationsStore } from '@/stores/NotificationsStore'
    import { useNotificationCenterStore } from '@/stores/NotificationCenterStore'
    import { hasAnyTripwire } from '@/utils/tripwireShape'

    import NotificationsSidebar from '@/components/NotificationsSidebar.vue'
    import ActionNotificationEntranceZone from '@/components/action_notifications/ActionNotificationEntranceZone.vue'
    import ActionNotificationExitZone from '@/components/action_notifications/ActionNotificationExitZone.vue'
    import ActionNotificationCombinedEEZone from '@/components/action_notifications/ActionNotificationCombinedEEZone.vue'
    import ActionNotificationWaitingZone from '@/components/action_notifications/ActionNotificationWaitingZone.vue'
    import ActionNotificationInteractionZone from '@/components/action_notifications/ActionNotificationInteractionZone.vue'
    import ActionNotificationPDDZone from '@/components/action_notifications/ActionNotificationPDDZone.vue'
    
    import { useGlobalState } from '@/composables/useGlobalState'

    import { io } from 'socket.io-client'

    import image from "./icons/logo_storalytic.png"
    import LanguagePicker from '@/components/ui/LanguagePicker.vue'

    export default {
        components: {
            NotificationsSidebar,
            LanguagePicker
        },
        
        props: {
            userId: Number,
            userName: String,
            userSurname: String,
            userRole: String,
            companyId: Number,
            companyName: String,
        },

        setup(props, { emit }) {
            const socket = ref(null)
            const isConnectingSocket = ref(false)
            const connectedOnce = ref(false)

            const toast = useToast()
            const { t } = useI18n()

            const route = useRoute()
            const router = useRouter()

            const userStore = useUserStore()
            const flowStore = useFlowStore()
            const companyStore = useCompanyStore()
            const cameraStreamStore = useCameraStreamStore()
            const detectionZoneStore = useDetectionZoneStore()
            const notificationsStore = useNotificationsStore()
            const notifCenter = useNotificationCenterStore()

            const showFlowList = ref(false)
            const toggleFlowList = () => {
                showFlowList.value = !showFlowList.value;

            }

            const expandedTypes = ref({})
            const toggleType = (typeId) => {
                expandedTypes.value[typeId] = !expandedTypes.value[typeId]
            }

            const toggleNotificationsPanel = () => {
                notifCenter.open ? notifCenter.close() : notifCenter.openTo('live')
            }

            const { serverConnection } = useGlobalState()
            const checkConnectionInterval = ref(null)
            const showedSuccessToast = ref(false)

            // State for sidebar visibility and screen size
            const sidebarOpen = ref(false)
            const isSmallScreen = ref(window.innerWidth <= 768)
            const toggleSidebar = () => {
                sidebarOpen.value = !sidebarOpen.value
            }

            const { userName, userSurname, userRole, companyName } = toRefs(props)

            // Computed property to determine if the current route is the registration route
            const isRegistrationRoute = computed(() => route.path === '/registration' || route.path === '/login')

            const unreadCount = computed(() => notificationsStore.unreadNotifications.length + notifCenter.allen.length)

            const isLoggedIn = computed(() => {
                return userStore.isUserLoggedIn
            })

            // ---- Geometry helpers (polygon/tripwire indicator in zone list) ----
            // After phase 2, zones may also carry a tripwire geometry. Until then,
            // tripwire fields are absent and these helpers gracefully report polygon-only.
            const tryParse = (str) => {
                if (!str) return null
                if (typeof str !== 'string') return str
                try { return JSON.parse(str) } catch { return null }
            }
            const zoneHasPolygon = (zone) => {
                const pts = tryParse(zone.DetectionZonePointArray)
                return Array.isArray(pts) && pts.length >= 3
            }
            const zoneHasTripwire = (zone) => hasAnyTripwire(zone.DetectionZoneTripwirePoints)
            const zoneGeometryLabel = (zone) => {
                const p = zoneHasPolygon(zone), tw = zoneHasTripwire(zone)
                if (p && tw) return t('nav.geometry.both')
                if (tw) return t('nav.geometry.tripwireOnly')
                return t('nav.geometry.polygon')
            }

            const groupedZonesByType = computed(() => {
                const grouped = {}

                const typeOrder = [
                    { id: 1, name: 'Entrance Zone' },
                    { id: 2, name: 'Exit Zone' },
                    { id: 3, name: 'Combined Entrance Exit Zone' },
                    { id: 4, name: 'Checkout Waiting Zone' },
                    { id: 5, name: 'Checkout Interaction Zone' },
                    { id: 6, name: 'Product Zone' },
                    { id: 7, name: 'Demo Zone' },
                    { id: 8, name: 'Display Zone' },
                    { id: 9, name: 'Info Zone' },
                    { id: 10, name: 'Service Zone' },
                    { id: 11, name: 'Pick Up Zone' },
                ]

                typeOrder.forEach(({ id, name }) => {
                    grouped[id] = {
                        label: name,
                        zones: detectionZoneStore.detectionzones.filter(z => z.DetectionZoneTypeId === id)
                    }
                })

                return grouped
            })

            const shouldRunConnectionLogic = () => {
                return isLoggedIn.value && !connectedOnce.value && !isRegistrationRoute.value
            }

            let previousPath = ref(route.path)

            const detectNetworkContext = async (deviceURL) => {
                try {
                    const controller = new AbortController()
                    const signal = controller.signal
                    const timeout = 5000 // 5 seconds timeout for checking public IP

                    // The box answers on its root (tunnel); the live relay on /status.
                    const probeUrl = isLiveRelayUrl(deviceURL) ? liveProbeUrl(deviceURL) : parseDeviceUrl(deviceURL).httpBase
                    const fetchPromise = fetch(probeUrl, { signal })
                    setTimeout(() => controller.abort(), timeout)

                    const response = await fetchPromise
                    if (response.ok) {
                        return deviceURL
                    }
                } catch (error) {
                    console.error('Device Socket URL is not accessible')
                    toast.error(t('nav.toast.deviceUnreachable'))
                    throw new Error('Device Socket URL is not accessible')
                }
                throw new Error('Device Socket URL is not accessible')
            }

            const establishSocketConnection = async (deviceURL) => {
                if (socket.value !== null) {
                    console.log("Returning existing socket")
                    return socket.value
                }

                if (isConnectingSocket.value) {
                    console.log('Already connecting, waiting...')
                    while (isConnectingSocket.value) {
                        await new Promise(r => setTimeout(r, 500))
                    }
                    return socket.value
                }

                console.log("Connecting socket ...")
                isConnectingSocket.value = true

                try {
                    const url = await detectNetworkContext(deviceURL)
                    //console.log('Network context detected, URL:', url)

                    // io(url) would read "/device/<id>" as a NAMESPACE, not a path prefix — it
                    // would ask for a namespace the edge does not define, over a socket.io path
                    // nginx does not route. Origin and path must be passed separately.
                    // For the legacy ":500x" shape these are the whole URL and "/socket.io",
                    // i.e. exactly what io(url) resolved to on its own. See @/api/deviceUrl.
                    const { origin, socketPath, proxied } = parseDeviceUrl(url)

                    socket.value = io(origin, {
                        path: socketPath,
                        // Same-origin once this store is cut over, so this is a no-op that
                        // records the intent. NOT unconditional: on the legacy cross-origin
                        // shape the edge replies "Access-Control-Allow-Origin: *", which a
                        // browser REFUSES for a credentialed request — it would break the
                        // live view of every store not yet cut over.
                        withCredentials: proxied,
                        transports: ['websocket'],
                        reconnection: true,
                        // Infinity, not 5. With 5 attempts and a 1s delay socket.io gives up
                        // ~5s after a blip and NEVER retries -- and establishSocketConnection
                        // returns the existing (dead) socket, so the only recovery is a page
                        // reload. A tunnel drop can legitimately last a minute (sshd reaps a
                        // dead reverse-tunnel session, freeing its 500x port, after
                        // ClientAliveInterval x CountMax). Keep trying instead.
                        reconnectionAttempts: Infinity,
                        reconnectionDelay: 1000,
                        reconnectionDelayMax: 10000,
                        debug: true,
                        perMessageDeflate: false,
                        timeout: 120000,
                        maxPayload: 50 * 1024 * 1024,
                    })

                    setupSocketEvents(socket.value)

                    return socket.value

                } catch (error) {
                    console.error('Error establishing socket connection:', error)
                    throw error
                } finally {
                    console.log('Resetting serverConnection flag')
                    isConnectingSocket.value = false
                }
            }

            const setupSocketEvents = (socketInstance) => {
                socketInstance.on('action_triggered', async (data) => {
                    const { detection_zone_type_id, detection_zone_name, passed_time,
                            detection_zone_id } = data

                    // docs/15 A+B: the in-portal pop is opt-out-able per user
                    // (RealtimeToast) AND filtered to the zones they subscribed to.
                    // The bell/center is the durable record and is filtered per-zone
                    // SERVER-SIDE regardless of either.
                    const wantRealtimeToast = userStore.user?.preferences?.RealtimeToast !== false
                    // Fail OPEN when the payload has no id (an edge still on the old
                    // emit) or the user never customised — better a toast too many
                    // than a silently dropped alert.
                    const zoneWanted = !zoneSubs.value.customised ||
                        detection_zone_id == null ||
                        zoneSubs.value.ids.has(detection_zone_id)
                    if (wantRealtimeToast && zoneWanted) {
                    if (detection_zone_type_id == 1) {
                        toast.warning({
                            component: ActionNotificationEntranceZone,
                            props: {
                                zone: detection_zone_name,
                            },
                            timeout: 10000
                        })
                    } else if (detection_zone_type_id == 2) {
                        toast.warning({
                            component: ActionNotificationExitZone,
                            props: {
                                zone: detection_zone_name,
                            },
                            timeout: 10000
                        })
                    } else if (detection_zone_type_id == 3) {
                        toast.warning({
                            component: ActionNotificationCombinedEEZone,
                            props: {
                                zone: detection_zone_name,
                                time: passed_time
                            },
                            timeout: 10000
                        })
                    } else if (detection_zone_type_id == 4) {
                        toast.warning({
                            component: ActionNotificationWaitingZone,
                            props: {
                                zone: detection_zone_name,
                                time: passed_time
                            },
                            timeout: 10000
                        })
                    } else if (detection_zone_type_id == 5) {
                        toast.warning({
                            component: ActionNotificationInteractionZone,
                            props: {
                                zone: detection_zone_name,
                                time: passed_time
                            },
                            timeout: 10000
                        })
                    } else if (detection_zone_type_id == 6 || detection_zone_type_id == 7 || detection_zone_type_id == 8) {
                        toast.warning({
                            component: ActionNotificationPDDZone,
                            props: {
                                zone: detection_zone_name,
                                time: passed_time
                            },
                            timeout: 10000
                        })
                    }
                    }

                    try {
                        await notificationsStore.fetchUnreadNotifications()
                    } catch (err) {
                        console.error("Failed to update notifications:", err)
                    }
                })

                socketInstance.on('disconnection_message', (data) => {
                    console.log('message on disconnection: ', data)
                })

                socketInstance.on('error', (error) => {
                    console.error('Error received from server:', error)
                    toast.error(t('nav.toast.error', { message: error.message }))
                })
            }

            // Add resize event listener when component is mounted
            onMounted( async () => {
                fetchUserDetailsIfNeeded()

                if (isLoggedIn.value) {
                    await userStore.fetchUserDetails()
                    await flowStore.fetchFlows()
                    await notificationsStore.fetchUnreadNotifications()
                    notifCenter.fetchAllen()

                     // only fetch company‐details if we have a selected company
                    const cid = userStore.currentCompany?.CompanyId
                    if (cid) {
                        try {
                            await companyStore.fetchCompanyDetails(cid)
                        } catch (err) {
                            console.error("Failed to load company details", err)
                        }
                        // Load the detection zones for the sidebar's zone-type
                        // groups. Previously these were populated as a side effect
                        // of the dashboard being the landing page; now that the
                        // Home (overview) is the landing, the sidebar loads its own.
                        try {
                            await detectionZoneStore.fetchDetectionZones()
                        } catch (err) {
                            console.error("Failed to load detection zones for sidebar", err)
                        }
                    }

                    // now that company is in place, init socket+health
                    if (shouldRunConnectionLogic()) {
                        await initConnection()
                    }

                    await checkConnectionStatus()
                    startConnectionCheckInterval()
                }

                window.addEventListener('resize', handleResize)
                handleResize() // Set initial state based on current window size
            })

            watch(isLoggedIn, async (loggedIn) => {
                if (loggedIn) {
                    await notificationsStore.fetchUnreadNotifications()
                }
            })

            
            watch(() => route.path, async (newPath) => {
                //Route changed, fetching user details if needed
                fetchUserDetailsIfNeeded()

                if (previousPath.value === '/login' && isLoggedIn.value) {
                    // If the user navigates from the login page and is logged in, check connection
                    await checkConnectionStatus()
                    startConnectionCheckInterval()
                }
                    // Update the previous path after each route change
                previousPath.value = newPath
            })

            watch(() => props.companyId, async (newCompanyId) => {
                if (!newCompanyId) return

                // Clear the badge immediately
                notificationsStore.unreadNotifications = []
                showedSuccessToast.value = false
                // Then re‐fetch
                await notificationsStore.fetchUnreadNotifications()

                // 3) ***new!*** re-load all the flows for this newly selected company
                await flowStore.fetchFlows()
                
                // (optionally reset your "expanded" state)
                showFlowList.value = false
                expandedTypes.value = {}
            }, { immediate: true })

            const checkConnectionStatus = async () => {
                // bail if no device URL
                const urlEDGEDEVICE = companyStore.company?.SocketioDeviceURL
                
                if (!urlEDGEDEVICE) {
                    serverConnection.value = false
                    return
                }

                const { httpBase, proxied } = parseDeviceUrl(urlEDGEDEVICE)
                // On LiveTransport 'iothub' the chip follows the box's IoT Hub heartbeat
                // (relay /status: online = a heartbeat in the last ~25 s), the same source
                // as the live screens. Otherwise it asks the box over the tunnel.
                const liveUrl = liveDeviceUrl(companyStore.company)
                const viaRelay = isLiveRelayUrl(liveUrl)

                try {
                    const response = viaRelay
                        ? await axios.get(liveProbeUrl(liveUrl), { withCredentials: true })
                        : await axios.get(`${httpBase}/check_connection`,
                                          { withCredentials: proxied })
                    if (response.status === 200 && (!viaRelay || response.data?.online === true)) {
                        serverConnection.value = true
                        if (!showedSuccessToast.value) {
                            toast.success(t('nav.toast.deviceConnected'))
                            showedSuccessToast.value = true
                        }
                    } else {
                        throw new Error('Connection failed')
                    }
                } catch (error) {
                    console.error('Connection check failed:', error)
                    if (serverConnection.value) {
                        toast.error(t('nav.toast.deviceConnectionLost'))
                        showedSuccessToast.value = false
                    }
                    serverConnection.value = false
                }
            }

            const startConnectionCheckInterval = () => {
                if (checkConnectionInterval.value) {
                    clearInterval(checkConnectionInterval.value)
                }
                // 20s, not 60s. This poll of <device>/check_connection is the ONLY thing
                // driving the "EdgAlytic connected" chip -- it is not tied to the socket at
                // all, so the chip can read "disconnected" while the real-time view is happily
                // receiving zone updates over the websocket. At 60s it also lagged recovery by
                // up to a minute. Observed both on 2026-08-17.
                checkConnectionInterval.value = setInterval(checkConnectionStatus, 20000)
            }

            const initConnection = async () => {
                // Alerts (action_triggered) from the box, or from the live relay on 'iothub'.
                const url = liveDeviceUrl(companyStore.company)
                if (!url) { return }

                try {
                    socket.value = await establishSocketConnection(url)
                    console.log('Socket connection NavSideBar established')
                    connectedOnce.value = true
                } catch (error) {
                    console.error('Failed to establish Nav Side Bar socket connection:', error)
                }
            }
            
            const fetchUserDetailsIfNeeded = async () => {
                const isAuthRoute = ['/login','/registration','/select-company'].includes(route.path)
                const hasToken = !!localStorage.getItem('authToken')

                if (isAuthRoute) {
                    // bail out on *any* public or select‑company page
                    return
                }

                if (hasToken && !userStore.user.UserName) {
                    await userStore.fetchUserDetails()

                    if (!userStore.user.UserName) {
                    // User details fetch failed, redirect to login
                    toast.error(t('nav.toast.credentialsInvalid'))
                    router.push('/login')
                    }
                }

                if (hasToken) await loadZoneSubs()
            }

            // Which zones this user wants real-time pops for (docs/15 B). The bell is
            // filtered server-side; the toast is filtered here, by id. Fails OPEN
            // (show everything) on error — a filter that silently hides alerts is
            // worse than one that shows too many.
            const zoneSubs = ref({ ids: new Set(), customised: false })
            const loadZoneSubs = async () => {
                try {
                    const zs = await userStore.fetchZoneSubscriptions()
                    zoneSubs.value = {
                        ids: new Set(zs.subscribedZoneIds || []),
                        customised: !!zs.customised,
                    }
                } catch (_) {
                    zoneSubs.value = { ids: new Set(), customised: false }
                }
            }

            const logout = async () => {
                // FIRST, while the credentials are still present: expire the
                // httpOnly device_session cookie server-side. JS cannot clear it
                // (known-gaps #3, Stage 1), and after the localStorage wipe below
                // the axios interceptor has no token left to authenticate with.
                await userStore.logoutServerSide()

                localStorage.removeItem('FixedWidgetsData')
                detectionZoneStore.clearDetectionZones()
                useZoneDirectoryStore().clear()
                cameraStreamStore.clearStreams()
                flowStore.clearFlows()
                userStore.clearUserState()
                companyStore.clearCompanyState()

                localStorage.removeItem('userDetails')
                localStorage.removeItem('authToken')

                // also wipe axios default auth header
                delete axios.defaults.headers.common['Authorization']
                // reset the in‑memory token too
                userStore.token = null

                // Check if the token is removed
                if (!localStorage.getItem('authToken')) {
                    console.log("Token successfully removed.")
                } else {
                    console.error("Failed to remove the token.")
                }
                
                router.push('/login').then(() => {
                    toast.info(t('nav.toast.loggedOut'))
                })
            }

            // Handling screen resize
            const handleResize = () => {
                isSmallScreen.value = window.innerWidth <= 768
                if (!isSmallScreen.value) {
                    sidebarOpen.value = true // Keep sidebar open on large screens
                } else {
                    sidebarOpen.value = false // Hide sidebar initially on small screens
                }
            }
  
            onBeforeUnmount(() => {
                if (checkConnectionInterval.value) {
                    clearInterval(checkConnectionInterval.value)
                }

                if (socket.value) {
                socket.value.off('action_triggered')
                socket.value.off('error')
                socket.value.off('connect')
                socket.value.disconnect()
            }

                // Remove the resize event listener when component is unmounted
                window.removeEventListener('resize', handleResize)
            })

            return {
                page: 'home',
                logo_storalytic: image,
                isRegistrationRoute,
                serverConnection,
                isLoggedIn,
                logout,
                notifCenter,
                toggleNotificationsPanel,
                unreadCount,
                userStore,
                companyStore,
                userName,
                userSurname,
                userRole,
                companyName,
                flowStore,
                toggleFlowList,
                showFlowList,
                groupedZonesByType,
                toggleType,
                expandedTypes,
                zoneHasPolygon,
                zoneHasTripwire,
                zoneGeometryLabel,
                sidebarOpen,
                isSmallScreen,
                toggleSidebar,
            }
        }
    }
</script>
  
<style scoped>
/* Backdrop for mobile */
.sidebar-backdrop{
  position:fixed; inset:0; background:rgba(10,14,18,.45);
  z-index:1095;
}

/* Shell */
.sidebar{
  position:fixed; top:0; left:0; z-index:1100;
  width:300px; height:100vh; display:flex; flex-direction:column;
  background:#ffffff;
  border-right:1px solid #e6edf3;
  box-shadow:2px 0 12px rgba(13,38,76,.08);
  padding:12px 12px 10px;
  overflow-y:auto;
  transition:transform .28s ease;
}

/* Mobile slide */
.sidebar.small-screen{ transform:translateX(-102%); height:100vh; }
.sidebar.open.small-screen{ transform:translateX(0); }

/* Close button (mobile) */
.close-btn{
  position:absolute; top:10px; right:12px;
  width:36px; height:36px; border-radius:8px;
  border:1px solid #e6edf3; background:#fff; color:#425461;
  display:inline-flex; align-items:center; justify-content:center;
}
.close-btn:hover{ background:#f0f4f8; }

/* Header band */
.sidebar-head{
  display:grid;
  grid-template-columns: 1fr auto;  /* logo left, bell right */
  grid-template-rows: auto auto;    /* company name on row 2 */
  align-items:center;
  gap:8px 10px;
  padding:6px 6px;
  background:#fff;
  border-radius:10px;
}
.sidebar-logo{
  grid-column:1 / 2;
  grid-row:1 / 2;
  height:80px;              /* cap the logo height */
  width:auto;               /* keep aspect ratio */
  object-fit:contain;
  display:block;
  margin:0;                 /* no extra space */
  margin-left: -15px;
}

.company-name{
  grid-column:1 / -1;       /* span both columns */
  grid-row:2 / 3;
  color:#425461; font-weight:600; font-size:.9rem;
  line-height:1.25;
  white-space:normal;       /* allow wrapping */
  overflow-wrap:anywhere;   /* break long words */
  word-break:break-word;    /* safety */
  margin-top:2px;
}

.bell-btn{
  grid-column:2 / 3;
  grid-row:1 / 2;
  position:relative;
  border:1px solid #e6edf3;
  background:#fff; color:#425461;
  width:36px; height:36px; border-radius:8px;
  display:inline-flex; align-items:center; justify-content:center;
  flex-shrink:0;
}
.bell-btn:hover{ background:#f0f4f8; }
.notification-badge{
  position:absolute; top:-4px; right:-4px;
  background:#C21500; color:#fff; border-radius:999px;
  padding:0 6px; font-size:.65rem; line-height:16px;
}

/* Tighten the divider so links start right after header */
.thin-divider{ border:0; border-top:1px solid #eef2f6; margin:6px 0; }
.thin-divider-sub{ border:0; border-top:1px dashed #e6edf3; margin:2px 14px; }

/* Section titles */
.sub-title{
  font-size:.92rem; color:#4F7EB3; font-weight:700;
  padding:6px 10px 2px; display:flex; align-items:center; gap:8px;
}

/* Home: top-level clickable item, aligned with the section titles */
.home-link{
  font-size:.92rem; color:#4F7EB3; font-weight:700;
  padding:6px 10px; display:flex; align-items:center; gap:8px;
  text-decoration:none; border-radius:8px; margin:2px 0;
  transition:background .15s ease;
}
.home-link:hover{ background:#f4f7fb; }
.home-link-active{ background:#eef5ff; }

/* Link lists */
.main-links{ padding:0 6px; margin-top:4px;}
.nav-link, .flow-link{
  display:block; text-decoration:none;
  color:#425461; font-size:.86rem; padding:6px 12px; border-radius:8px;
  margin:2px 6px; transition:background .15s ease, color .15s ease;
}
.nav-link:hover, .flow-link:hover{ background:#f4f7fb; color:#4F7EB3; }

/* Active pill */
.active-link{
  color:#4F7EB3 !important; background:#eef5ff;
  border-left:3px solid #B2C149; padding-left:9px;
  font-weight:700;
}

/* Nested lists */
.flow-list, .detection-zone-list{ padding:4px 6px 4px 28px; }

/* Geometry indicator before a zone name */
.geom-glyph{
  display:inline-flex; align-items:center; gap:3px;
  margin-right:6px; color:#7a8a96; font-size:.78rem;
}
.flow-link.active-link .geom-glyph,
.flow-link:hover .geom-glyph{ color:#4F7EB3; }

/* Expandable headers */
.expandable-header-cameras,
.expandable-header-detection-zones{
  display:flex; align-items:center; gap:8px; cursor:pointer;
  color:#425461; font-size:.86rem; padding:8px 12px; margin:2px 6px; border-radius:8px;
}
.expandable-header-cameras:hover,
.expandable-header-detection-zones:hover{ background:#f7f9fb; }
.triangle-icon{
  display:inline-block; width:0; height:0;
  border-left:5px solid transparent; border-right:5px solid transparent; border-top:6px solid #425461;
  transform:rotate(-90deg); transition:transform .2s ease;
}
.triangle-icon.expanded{ transform:rotate(0deg); }

/* Footer */
.sidebar-footer{ margin-top:auto; padding:8px 6px 4px; color:#425461; font-size:.75rem; }
.connection-status{ display:flex; flex-direction:column; align-items:flex-start; gap:6px; }
.status-label{ font-weight:700; color:#425461; }

.chip{
  display:inline-flex; align-items:center; gap:6px;
  border-radius:999px; padding:4px 10px; font-size:.78rem;
  border:1px solid #e6edf3; color:#4F7EB3; background:#f7fbff;
}
.chip-ok{ color:#2c7a3f; background:#f3fbf5; border-color:#d9f1df; }
.chip-warn{ color:#b66a00; background:#fff7e6; border-color:#ffe7bf; }

/* Links aligned with the left edge like the labels/icons above */
.footer-link{
  padding:6px 0; margin:0; border-radius:0;
  color:#425461; font-size:.76rem;
}
.footer-link:hover{ background:transparent; color:#4F7EB3; }

/* user row in one line, wraps nicely if needed */
.settings-container .user-row{
  display:flex; align-items:center; flex-wrap:wrap;
  column-gap:2px;                 /* space between items on same line */
  row-gap:4px;                    /* tight gap when it wraps */
  padding:4px 0;
  color:#425461; font-size:.78rem;
}

/* name wraps naturally */
.settings-container .user-row .user-name{ white-space:normal; }

.settings-container .user-row i.bi-person-fill{
  margin-right: 1px; /* explicit icon gap before the name */
}

/* small gap around the dot */
.settings-container .user-row .dot{
  color:#9aa8b3;
  margin:0 6px;
}

/* button styled as a link; pointer cursor */
.settings-container .user-row .logout-link-btn{
  appearance:none; -webkit-appearance:none;
  background:transparent; border:0; padding:0;
  color:#4F7EB3; font:inherit; cursor:pointer;
  line-height:1.2;
}
.settings-container .user-row .logout-link-btn:hover{ text-decoration:underline; }

/* if you MUST keep <a class="logout-link"> instead of the button, enforce inline */
.settings-container .user-row .logout-link{
  display:inline !important;
  margin:0 !important; padding:0 !important;
  color:#4F7EB3; cursor:pointer; text-decoration:none;
}
.settings-container .user-row .logout-link:hover{ text-decoration:underline; }

/* Top bar icons (mobile) */
.hamburger{
  position:fixed; top:10px; left:10px; z-index:1100;
  font-size:26px; color:#425461; cursor:pointer;
}
.mobile-bell{
  position:fixed; top:12px; left:54px; z-index:1100; color:#425461; cursor:pointer;
}
.notification-bell{ position:relative; }

/* Scrollbar (webkit) */
.sidebar::-webkit-scrollbar{ width:10px; }
.sidebar::-webkit-scrollbar-thumb{ background:#e6edf3; border-radius:10px; }
.sidebar::-webkit-scrollbar-thumb:hover{ background:#d7e2ee; }

/* Responsive width */
@media (max-width: 992px){ .sidebar{ width:280px; } }
@media (max-width: 768px){ 
    .sidebar{ width:56vw; }

    /* move the close a hair inward & smaller */
    .close-btn{
        top: 8px;
        right: 8px;
        width: 32px;
        height: 32px;
    }

    /* reserve space on the right of the header equal to the close button width */
    .sidebar-head{
        padding-right: 56px;   /* 32px button + ~24px breathing room */
    }

    /* nudge/smaller bell so it never bumps the close button */
    .bell-btn{
        width: 32px;
        height: 32px;
        margin-right: 4px;      /* tiny inset from the header edge */
        justify-self: end;      /* keep it at the right of grid column */
    }

    .notification-badge{
        top: -3px;
        right: -3px;
    }
}
</style>